// @flow

import React from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "connected-react-router";
import { SectionComponent, Loader, StatTileGroup } from "@components";
import type { State } from "@store/reducers";
import { isFirstCareUser, isClientAdmin } from "@modules/account/selectors";
import * as reportingSelectors from "@modules/reporting/selectors";
import type { ValuesItemValue } from "@modules/reporting/reducer/types";
import type { DefaultReports } from "@modules/reporting/reducer/reports/absence-details/types";
import actionCreators from "@modules/reporting/actionCreator";
import { defaultReports } from "@modules/reporting/selectors/reports/absence-details";
import { toolsDashboardTodayKeys, statItemPrefixMap } from "@modules/reporting/constants/dashboard";
import { DashboardAbsenceNow } from "@modules/reporting/components";
import { route } from "@routes/helper";
import { getIsDashboardToolsLoading } from "@modules/reporting/selectors/dashboard/dashboard-index";
import * as Paths from "@routes/constants";

import "./styles.scss";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  today: ValuesItemValue[],
  isFirstCareUser: boolean,
  isClientAdmin: boolean,
  defaultReports: Array<DefaultReports>,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceDetailsCharts: typeof actionCreators.GetAbsenceDetailsCharts,
  getAbsenceDetailsReport: typeof actionCreators.GetAbsenceDetailsReport,
  push: typeof push,
}>;

type Props = {
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
};

class DashboardOverviewSection extends React.Component<Props> {
  get todayStatItems() {
    let statItemOptions;
    return this.props.today
      .map(item => {
        statItemOptions = this.todayStatItemsMap[item.key];
        if (statItemOptions) {
          return {
            title: this.props.t(statItemOptions.title || item.description),
            value: item.value,
            prefix: item.type && statItemPrefixMap[item.type],
            onClick: statItemOptions.clickHandler,
            className: statItemOptions?.className,
          };
        }
        return null;
      })
      .filter(item => item);
  }

  navigationHandler = (path: $Keys<typeof Paths>) => () =>
    this.props.push(
      route(path, {
        clientId: this.props.clientId,
      }),
    );

  get todayStatItemsMap() {
    return {
      [toolsDashboardTodayKeys.absentStaff]: {
        className: "text-black item-value__sub-value_big",
        path: "REPORTING_REPORTS_ABSENCE_DETAILS_REPORT_PATH",
        clickHandler: this.navigateToOpenAbsenceReport,
        title: "OPEN_ABSENCES",
      },
      [toolsDashboardTodayKeys.absenceRate]: {
        className: "text-black item-value__sub-value_big",
        path: "REPORTING_TRENDS_ABSENCE_RATE_PATH",
        clickHandler: this.navigationHandler("REPORTING_TRENDS_ABSENCE_RATE_PATH"),
        title: "PERCENT_OF_ABSENT",
      },
      [toolsDashboardTodayKeys.outstandingActions]: {
        className: "text-black item-value__sub-value_big ",
        path: "REPORTING_TOOLS_TASKS_PATH",
        clickHandler: this.navigationHandler("REPORTING_TOOLS_TASKS_PATH"),
        title: "OUTSTANDING_TASKS",
      },
      [toolsDashboardTodayKeys.newAlerts]: {
        className: "text-black item-value__sub-value_big",
        path: "REPORTING_REPORTS_ALERT_DETAILS_PATH",
        clickHandler: this.navigationHandler("REPORTING_REPORTS_ALERT_DETAILS_PATH"),
        title: "RECENT_ALERTS",
      },
      [toolsDashboardTodayKeys.newNotifications]: {
        className: "text-black item-value__sub-value_big ",
        title: "RECENT_NOTIFICATIONS",
        clickHandler: this.navigationHandler("REPORTING_TOOLS_NOTIFICATION"),
        path: "REPORTING_TOOLS_NOTIFICATION",
      },
    };
  }

  navigateToOpenAbsenceReport = () => {
    const defaultOpenAbsencesReport = this.props.defaultReports.find(
      report => report.name === this.props.t("CURRENT_OPEN_ABSENCES"),
    );

    if (defaultOpenAbsencesReport) {
      this.props.getAbsenceDetailsCharts({
        clientId: this.props.clientId,
        filters: defaultOpenAbsencesReport ? defaultOpenAbsencesReport.filters : {},
      });
      this.props.getAbsenceDetailsReport({
        clientId: this.props.clientId,
        report: defaultOpenAbsencesReport || {},
        pageable: {
          page: 1,
        },
        callBack: () =>
          this.props.push(
            route("REPORTING_REPORTS_ABSENCE_DETAILS_REPORT_PATH", {
              clientId: this.props.clientId,
            }),
          ),
      });
    }
  };

  render() {
    return (
      <>
        {this.props.isLoading && <Loader inline />}
        <StatTileGroup data={this.todayStatItems} className="gs-stats" />
        <DashboardAbsenceNow clientId={this.props.clientId} />
      </>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  today: reportingSelectors.getToolsDashboardToday(state),
  isFirstCareUser: isFirstCareUser(state),
  isClientAdmin: isClientAdmin(state),
  defaultReports: defaultReports(state),
  isLoading: getIsDashboardToolsLoading(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceDetailsCharts: actionCreators.GetAbsenceDetailsCharts,
  getAbsenceDetailsReport: actionCreators.GetAbsenceDetailsReport,
  push,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(DashboardOverviewSection);
